import { fetchData } from "@/tabs/Operation/utils";
import { getConfig, klinikenApi } from "@/api";

const PENDING_RESPONSE = "Inget resultat tillgängligt";

/**
 * Startar en asynkron Celery-task via POST och pollar sedan via GET tills ett resultat finns.
 *
 * @param {string} startUrl - URL att POST:a för att starta tasken
 * @param {(taskId: string) => string} buildPollUrl - bygger GET-poll-URL:en givet task_id
 * @param {object} [options]
 * @param {number} [options.intervalMs=5000]
 * @param {number} [options.maxWaitMs=360000]
 * @param {object} [options.postConfig={}] - extra axios-konfiguration som slås ihop med POST-anropet
 * @param {(taskId: string) => Promise<any>} [options.fetchPollResult] - anpassad pollfunktion;
 *   returnera undefined medan den väntar, returnera ett värde när det är klart, kasta typat fel vid misslyckande.
 *   Standard är en JSON-hanterare som kontrollerar exc_type och PENDING_RESPONSE.
 * @returns {{ promise: Promise<any>, cancel: () => void }}
 *
 * Promise:n rejectar med ett av:
 *   { type: "cancelled" }                              – cancel() anropades
 *   { type: "task_failure", exc_type, exc_message }    – backend-task kastade ett undantag
 *   { type: "timeout" }                                – maxWaitMs överskreds
 *   { type: "http_error", error }                      – nätverks- eller HTTP-fel
 */
export function startAsyncTask(
    startUrl,
    buildPollUrl,
    { intervalMs = 5_000, maxWaitMs = 360_000, postConfig = {}, fetchPollResult } = {}
) {
    let cancelled = false;
    let intervalId = null;
    let _reject = null;

    const _fetchPollResult =
        fetchPollResult ??
        (async (taskId) => {
            const result = await fetchData({ url: buildPollUrl(taskId) });
            if (result === PENDING_RESPONSE) return undefined;
            if (result?.exc_type)
                throw { type: "task_failure", exc_type: result.exc_type, exc_message: result.exc_message };
            return result;
        });

    const promise = new Promise(async (resolve, reject) => {
        _reject = reject;

        let startResponse;
        try {
            startResponse = await klinikenApi.post(startUrl, {}, getConfig(postConfig));
        } catch (err) {
            reject({ type: "http_error", error: err });
            return;
        }

        if (startResponse.status !== 200) {
            reject({ type: "http_error", error: new Error(`HTTP ${startResponse.status}`) });
            return;
        }

        const taskId = startResponse.data.task_id;
        const maxAttempts = maxWaitMs / intervalMs;
        let attempts = 0;
        let pollInProgress = false;

        intervalId = setInterval(async () => {
            if (cancelled) return;
            // Hoppa över detta tick om föregående poll fortfarande pågår, så att
            // ett anrop som tar längre än intervallet inte race:ar mot nästa.
            if (pollInProgress) return;
            pollInProgress = true;

            let result;
            try {
                result = await _fetchPollResult(taskId);
            } catch (err) {
                clearInterval(intervalId);
                intervalId = null;
                reject(err?.type ? err : { type: "http_error", error: err });
                return;
            } finally {
                pollInProgress = false;
            }

            if (cancelled) return;

            if (result !== undefined) {
                clearInterval(intervalId);
                intervalId = null;
                resolve(result);
                return;
            }

            attempts++;
            if (attempts >= maxAttempts) {
                clearInterval(intervalId);
                intervalId = null;
                reject({ type: "timeout" });
            }
        }, intervalMs);
    });

    const cancel = () => {
        if (cancelled) return;
        cancelled = true;
        if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
        }
        _reject?.({ type: "cancelled" });
    };

    return { promise, cancel };
}
